<template>
	<v-card dark outlined style="background:#1E1E1E;">

		<!-- Header row -->
		<v-card-title class="py-2 px-3 d-flex align-center justify-space-between">
			<div class="d-flex align-center">
				<v-icon small left color="cyan lighten-1">mdi-table</v-icon>
			<span class="caption font-weight-bold text-uppercase grey--text text--lighten-1">
				{{ $ml.get('alarm_stats_stoppage_log') }}
			</span>
			</div>
		<span class="caption" style="color:#26C6DA;">
			{{ $ml.get('alarm_stats_total_idle') }}: {{ formattedTotalIdle }}
		</span>
		</v-card-title>

		<v-divider dark></v-divider>

		<!-- Data table -->
		<v-data-table
			:headers="headers"
			:items="sortedStoppages"
			:sort-by="['started_at']"
			:sort-desc="[true]"
			dark
			dense
			hide-default-footer
			:items-per-page="-1"
			:no-data-text="$ml.get('alarm_stats_no_stats')"
			class="elevation-0 stoppage-table"
			style="background:#1E1E1E;"
		>
			<!-- # column -->
			<template v-slot:item.id="{ item }">
				<span class="caption grey--text">{{ item.id }}</span>
			</template>

			<!-- START TIME column -->
			<template v-slot:item.started_at="{ item }">
				<span class="caption">{{ formatTime(item.started_at) }}</span>
			</template>

			<!-- END TIME column -->
			<template v-slot:item.ended_at="{ item }">
				<span class="caption">{{ item.ended_at ? formatTime(item.ended_at) : '—' }}</span>
			</template>

			<!-- DURATION column -->
			<template v-slot:item.duration="{ item }">
				<span class="caption">
					{{ item.ended_at ? formatDuration(getDurationMs(item)) : $ml.get('alarm_stats_active_dash') }}
				</span>
			</template>

			<!-- CAMERA TRIGGER column -->
			<template v-slot:item.device_id="{ item }">
				<span class="caption">{{ item.device_id || '—' }}</span>
			</template>

			<!-- RATE BEFORE column -->
			<template v-slot:item.rate_before="{ item }">
				<span class="caption">{{ item.rate_before != null ? item.rate_before : '—' }}</span>
			</template>

			<!-- RATE AFTER column -->
			<template v-slot:item.rate_after="{ item }">
				<span class="caption">
					{{ item.ended_at && item.rate_after != null ? item.rate_after : '—' }}
				</span>
			</template>

			<!-- STATUS column -->
			<template v-slot:item.status="{ item }">
				<v-chip
					x-small
					:color="item.ended_at ? 'green darken-1' : 'red darken-1'"
					text-color="white"
				>
					{{ item.ended_at ? $ml.get('alarm_stats_resolved') : $ml.get('alarm_stats_active') }}
				</v-chip>
			</template>

			<!-- NOTES column — click-to-edit -->
			<template v-slot:item.notes="{ item }">
			<v-edit-dialog
				:return-value.sync="item.notes"
				large
				persistent
				:cancel-text="$ml.get('alarm_stats_cancel')"
				:save-text="$ml.get('save')"
				dark
				@save="handleNoteSave(item)"
			>
					<!-- display value -->
					<span class="caption" style="cursor:pointer;min-width:80px;display:inline-block;">
						{{ item.notes || '—' }}
						<v-icon x-small class="ml-1" color="grey">mdi-pencil-outline</v-icon>
					</span>

					<!-- edit slot -->
					<template v-slot:input>
						<v-text-field
					v-model="item.notes"
					:label="$ml.get('alarm_stats_note_placeholder')"
							single-line
							autofocus
							dense
							dark
							style="min-width:200px;"
						></v-text-field>
					</template>
				</v-edit-dialog>
			</template>

		</v-data-table>

	</v-card>
</template>

<script>
export default {
	name : 'StatsStoppageLog',

	props : {
		stoppages : {
			type    : Array,
			default : () => [],
		},
		totalIdleMs : {
			type    : Number,
			default : 0,
		},
	},

	computed : {
		headers() {
			return [
				{ text: '#',                                        value: 'id',         sortable: false, width: '40px' },
				{ text: this.$ml.get('alarm_stats_start_time'),    value: 'started_at', sortable: true  },
				{ text: this.$ml.get('alarm_stats_end_time'),      value: 'ended_at',   sortable: false },
				{ text: this.$ml.get('alarm_stats_duration'),       value: 'duration',   sortable: false },
				{ text: this.$ml.get('alarm_stats_camera_trigger'), value: 'device_id',  sortable: false },
				{ text: this.$ml.get('alarm_stats_rate_before'),    value: 'rate_before',sortable: false },
				{ text: this.$ml.get('alarm_stats_rate_after'),     value: 'rate_after', sortable: false },
				{ text: this.$ml.get('alarm_stats_status'),         value: 'status',     sortable: false },
				{ text: this.$ml.get('alarm_stats_notes'),          value: 'notes',      sortable: false                 },
			];
		},
		/** Sort stoppages by started_at descending (most recent first). */
		sortedStoppages() {
			if (!this.stoppages || this.stoppages.length === 0) return [];
			return [...this.stoppages].sort((a, b) => {
				const ta = a.started_at ? new Date(a.started_at).getTime() : 0;
				const tb = b.started_at ? new Date(b.started_at).getTime() : 0;
				return tb - ta;
			});
		},

		/** Human-readable total idle time from totalIdleMs prop. */
		formattedTotalIdle() {
			return this.formatDuration(this.totalIdleMs);
		},
	},

	methods : {
		formatTime(ts) {
			if (!ts) return '—';
			try {
				const m = this.$momentz.tz(ts, this.$timezone);
				return m.isValid() ? m.format('M/D/YYYY, h:mm A') : String(ts);
			} catch (e) {
				return String(ts);
			}
		},

		/**
		 * Compute duration in ms from a stoppage object (ended_at - started_at).
		 */
		getDurationMs(item) {
			if (!item.started_at || !item.ended_at) return 0;
			const start = new Date(item.started_at).getTime();
			const end   = new Date(item.ended_at).getTime();
			return Math.max(0, end - start);
		},

		/**
		 * Format ms to "Xm Ys" string.
		 */
		formatDuration(ms) {
			if (!ms || ms <= 0) return '0s';
			const totalSeconds = Math.floor(ms / 1000);
			const hours        = Math.floor(totalSeconds / 3600);
			const minutes      = Math.floor((totalSeconds % 3600) / 60);
			const seconds      = totalSeconds % 60;
			if (hours > 0)   return `${hours}h ${minutes}m`;
			if (minutes > 0) return `${minutes}m ${seconds}s`;
			return `${seconds}s`;
		},

		/**
		 * Called when user saves a note via v-edit-dialog.
		 * Emits update:note so the parent can persist via API (T-014+).
		 */
		handleNoteSave(item) {
			this.$emit('update:note', {
				stoppageId : item.id,
				note       : item.notes,
			});
		},
	},
};
</script>

<style scoped>
.stoppage-table >>> .v-data-table__wrapper table thead tr th {
	font-size   : 0.7rem !important;
	color       : #888888 !important;
	font-weight : 600;
	letter-spacing: 0.04em;
}
.stoppage-table >>> .v-data-table__wrapper table tbody tr td {
	font-size : 0.75rem !important;
}
.stoppage-table >>> .v-data-table__wrapper table tbody tr:hover {
	background : #2A2A2A !important;
}
</style>
